/** ISO `YYYY-MM-DD` date helpers; ISO strings rather than `Date` on purpose, so the browser's timezone never moves a planned day. */

import { businessTodayIso } from '../businessDate'

/** Today in Ulaanbaatar, not the browser's day. */
export function todayIso(): string {
  return businessTodayIso()
}

/** Parse an ISO day into a UTC-noon Date. Noon so a DST shift cannot move it. */
function parse(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, 12))
}

function format(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`
}

export function addDays(iso: string, days: number): string {
  const d = parse(iso)
  d.setUTCDate(d.getUTCDate() + days)
  return format(d)
}

/** Day-of-month is kept and clamped to the target month's last day (31 Jan + 1 -> 28 Feb). Not `pages/time/dates.addMonths`, which snaps to the 1st. */
export function addMonthsClamped(iso: string, months: number): string {
  const d = parse(iso)
  const day = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + months)
  // Clamp: adding a month to the 31st must not roll into the next one.
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, last))
  return format(d)
}

export function daysBetween(a: string, b: string): number {
  return Math.round((parse(b).getTime() - parse(a).getTime()) / 86_400_000)
}

export function dayRange(from: string, to: string): string[] {
  const out: string[] = []
  const n = daysBetween(from, to)
  for (let i = 0; i <= n; i += 1) out.push(addDays(from, i))
  return out
}

export function isMonthStart(iso: string): boolean {
  return iso.endsWith('-01')
}

export function weekday(iso: string): number {
  return parse(iso).getUTCDay()
}

export function isWeekend(iso: string): boolean {
  const w = weekday(iso)
  return w === 0 || w === 6
}

export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

export function dayNum(iso: string): number {
  return Number(iso.slice(8, 10))
}

export function clampToHorizon(iso: string, start: string, end: string): string {
  if (iso < start) return start
  if (iso > end) return end
  return iso
}

/** The range of days the grid holds; both ends inclusive. */
export interface DayWindow {
  from: string
  to: string
}

/**
 * The window the schedule opens on and grows from: symmetric around the anchor for every view.
 * Clamping SHORTENS rather than shifting: a short window at the edge of the horizon is the edge.
 */
export function windowAround(
  asOf: string,
  backDays: number,
  forwardDays: number,
  start: string,
  end: string,
): DayWindow {
  return {
    from: clampToHorizon(addDays(asOf, -backDays), start, end),
    to: clampToHorizon(addDays(asOf, forwardDays), start, end),
  }
}

/** Grow a window by `chunk` days at one end, capped at `maxDays`; past the cap the FAR end gives ground. Returns the same object when nothing can move so a scroll handler can bail out. */
export function growWindow(
  window: DayWindow,
  direction: 'back' | 'forward',
  chunk: number,
  maxDays: number,
  start: string,
  end: string,
): DayWindow {
  if (direction === 'back') {
    if (window.from <= start) return window
    const from = clampToHorizon(addDays(window.from, -chunk), start, end)
    const to =
      daysBetween(from, window.to) + 1 > maxDays ? addDays(from, maxDays - 1) : window.to
    return { from, to }
  }
  if (window.to >= end) return window
  const to = clampToHorizon(addDays(window.to, chunk), start, end)
  const from =
    daysBetween(window.from, to) + 1 > maxDays ? addDays(to, -(maxDays - 1)) : window.from
  return { from, to }
}

const MONTH_KEYS = [
  'month.jan',
  'month.feb',
  'month.mar',
  'month.apr',
  'month.may',
  'month.jun',
  'month.jul',
  'month.aug',
  'month.sep',
  'month.oct',
  'month.nov',
  'month.dec',
]

export function monthKey(iso: string): string {
  return MONTH_KEYS[Number(iso.slice(5, 7)) - 1]
}

const WEEKDAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/** The `weekday.<code>` i18n key's code, not `Intl`: browsers ship without Mongolian locale data and would print English. */
export function weekdayCode(iso: string): (typeof WEEKDAY_CODES)[number] {
  return WEEKDAY_CODES[weekday(iso)]
}

/** Add `months` to an ISO expiry base; `months === 0` means never expires and returns null. */
export function expiryOf(issued: string, months: number): string | null {
  if (!months) return null
  return addMonthsClamped(issued, months)
}
